/**
 * Shopify-Katalog-Sync (PIM Phase 2) — "Inhalt runter".
 *
 * Shopify = Content-Master. Dieser Sync spiegelt Titel, Beschreibung,
 * Varianten (SKU/Optionen/Online-Preise) und Bild-URLs nach FieldConnect.
 *
 * DATENSCHUTZ-REGELN (Live-System!):
 *   • Niemals angefasst: products.preis (B2B), stock/reserved (Stock-Engine),
 *     lagerort, Kundenpreise, melde/hoechst_bestand.
 *   • Varianten: Upsert per SKU; online_preis/compare_at kommen von Shopify,
 *     b2b_preis/bestand bleiben unberührt. Entfernte Varianten → aktiv=false.
 *   • Bilder: reiner Spiegel (delete+insert der CDN-URLs).
 *   • Gewicht/Grundpreis: Shopify-Spiegel (nur wenn in Shopify gesetzt);
 *     grundpreis_anzeigen bleibt lokal.
 *   • Neue Shopify-Produkte werden NIE automatisch angelegt — nur nach
 *     expliziter Auswahl im Report (applyImport).
 */
import { base44 } from '@/api/base44Client';
import { fromShopifyUnitPrice, weightToKg } from '@/lib/grundpreis';
import { findLikelyDuplicate } from '@/lib/productNameMatch';
import { shopifyGraphQL } from '@/lib/shopifyClient';

const CATALOG_QUERY = `
  query CatalogProducts($first: Int!, $after: String) {
    products(first: $first, after: $after) {
      pageInfo { hasNextPage endCursor }
      nodes {
        id
        title
        descriptionHtml
        status
        images(first: 20) { nodes { id url altText } }
        variants(first: 100) {
          nodes {
            id
            sku
            title
            price
            compareAtPrice
            inventoryQuantity
            selectedOptions { name value }
            unitPriceMeasurement { quantityValue quantityUnit referenceValue referenceUnit }
            inventoryItem { id measurement { weight { value unit } } }
          }
        }
      }
    }
  }
`;

/** Alle Shopify-Produkte (paginiert) laden */
export async function fetchShopifyCatalog(onProgress) {
  const all = [];
  let after = null;
  for (let page = 0; page < 40; page++) {
    const data = await shopifyGraphQL(CATALOG_QUERY, { first: 50, after });
    const conn = data?.products;
    if (!conn) break;
    all.push(...conn.nodes);
    onProgress?.(all.length);
    if (!conn.pageInfo?.hasNextPage) break;
    after = conn.pageInfo.endCursor;
  }
  return all;
}

const normSku = (s) => (s || '').trim();

/**
 * Analyse (rein lesend): Shopify-Katalog gegen FC-Produkte matchen.
 * @returns {matched, newInShopify, fcOnlyCount, skuless, duplicateSkus}
 */
export async function analyzeCatalog(onProgress) {
  const [shopifyProducts, fcProducts, fcVariants] = await Promise.all([
    fetchShopifyCatalog(onProgress),
    base44.entities.Product.list('name'),
    base44.entities.ProductVariant.list('position'),
  ]);

  // FC-Index 1: bereits verknüpfte Shopify-Produkt-IDs (stärkster Schlüssel —
  // überlebt SKU-Änderungen in Shopify)
  const fcByShopifyId = new Map();
  fcProducts.forEach((p) => { if (p.shopify_product_id) fcByShopifyId.set(p.shopify_product_id, p); });

  // FC-Index 2: SKU (Produkt-SKU + Varianten-SKUs), case-insensitiv
  const key = (s) => normSku(s).toLowerCase();
  const fcBySku = new Map();
  fcProducts.forEach((p) => {
    const k = key(p.sku);
    if (k && !fcBySku.has(k)) fcBySku.set(k, p);
  });
  const variantsByProduct = new Map();
  fcVariants.forEach((v) => {
    const list = variantsByProduct.get(v.product_id) || [];
    list.push(v);
    variantsByProduct.set(v.product_id, list);
    const k = key(v.sku);
    if (k && !fcBySku.has(k)) {
      const p = fcProducts.find((x) => x.id === v.product_id);
      if (p) fcBySku.set(k, p);
    }
  });

  // Shopify-interne SKU-Dubletten: dieselbe SKU an VERSCHIEDENEN Produkten
  const shopifySkuOwners = new Map();
  shopifyProducts.forEach((sp) => {
    sp.variants.nodes.forEach((v) => {
      const k = key(v.sku);
      if (!k) return;
      const owners = shopifySkuOwners.get(k) || new Set();
      owners.add(sp.id);
      shopifySkuOwners.set(k, owners);
    });
  });
  const isShopifyDupSku = (k) => (shopifySkuOwners.get(k)?.size || 0) > 1;

  const matched = [];
  const newInShopify = [];
  const skuless = [];
  const skuConflicts = [];
  const matchedFcIds = new Set();
  const claimedSkus = new Set(); // SKUs, die in DIESEM Lauf bereits einem Produkt zugeordnet wurden

  shopifyProducts.forEach((sp) => {
    const keys = sp.variants.nodes.map((v) => key(v.sku)).filter(Boolean);

    // Priorität 1: bereits verknüpft (shopify_product_id)
    const linked = fcByShopifyId.get(sp.id);
    if (linked) {
      matched.push({ shopify: sp, fcProduct: linked, fcVariants: variantsByProduct.get(linked.id) || [] });
      matchedFcIds.add(linked.id);
      keys.forEach((k) => claimedSkus.add(k));
      return;
    }

    if (keys.length === 0) { skuless.push({ shopify: sp }); return; }

    // Priorität 2: SKU — aber nur, wenn die SKU eindeutig ist
    const conflict = keys.find((k) => isShopifyDupSku(k) || claimedSkus.has(k));
    const fc = keys.map((k) => fcBySku.get(k)).find(Boolean);
    if (fc && !matchedFcIds.has(fc.id) && !conflict) {
      matched.push({ shopify: sp, fcProduct: fc, fcVariants: variantsByProduct.get(fc.id) || [] });
      matchedFcIds.add(fc.id);
      keys.forEach((k) => claimedSkus.add(k));
    } else if (conflict || (fc && matchedFcIds.has(fc.id))) {
      // Dieselbe SKU gehört schon einem anderen Produkt → NIE importieren
      skuConflicts.push({
        shopify: sp,
        sku: conflict || keys[0],
        reason: isShopifyDupSku(conflict || keys[0])
          ? 'SKU in Shopify mehrfach vergeben (verschiedene Produkte)'
          : 'SKU bereits einem anderen FieldConnect-Produkt zugeordnet',
      });
    } else {
      newInShopify.push({ shopify: sp });
      keys.forEach((k) => claimedSkus.add(k));
    }
  });

  const fcOnlyCount = fcProducts.filter((p) => !matchedFcIds.has(p.id)).length;

  // Dubletten-Warnung: Ein neues Shopify-Produkt kann derselbe Artikel sein wie ein
  // vorhandener (oft inaktiver, SKU-loser) FC-Datensatz. Würde er blind importiert,
  // blieben Kundenpreise, Belege und Lagerbewegungen am alten Datensatz hängen.
  const unmatchedFc = fcProducts.filter((p) => !matchedFcIds.has(p.id));
  newInShopify.forEach((item) => {
    const treffer = findLikelyDuplicate(item.shopify.title, unmatchedFc);
    if (treffer) {
      item.possibleDuplicate = {
        id: treffer.id,
        name: treffer.name,
        sku: treffer.sku || '',
        is_active: treffer.is_active !== false,
      };
    }
  });

  return { matched, newInShopify, skuless, skuConflicts, fcOnlyCount, totalShopify: shopifyProducts.length };
}

/** Bild-Spiegel eines Produkts ersetzen (delete + insert) */
async function mirrorImages(productId, shopifyProduct) {
  try {
    const existing = await base44.entities.ProductImage.filter({ product_id: productId });
    for (const img of existing) {
      await base44.entities.ProductImage.delete(img.id);
    }
  } catch { /* Erststart: Tabelle leer */ }
  const nodes = shopifyProduct.images?.nodes || [];
  for (let i = 0; i < nodes.length; i++) {
    await base44.entities.ProductImage.create({
      product_id: productId,
      url: nodes[i].url,
      alt: nodes[i].altText || '',
      position: i + 1,
      shopify_image_id: nodes[i].id,
    });
  }
  return nodes[0]?.url || null;
}

/** Spalten der Gewicht/Grundpreis-Migration (können in Prod noch fehlen) */
const GEWICHT_GRUNDPREIS_KEYS = [
  'gewicht_kg', 'gewicht_synced_at',
  'grundpreis_menge', 'grundpreis_einheit',
  'grundpreis_referenz_menge', 'grundpreis_referenz_einheit',
];

/**
 * Varianten-Write mit Fallback: Ist die Gewicht/Grundpreis-Migration noch
 * nicht eingespielt, meldet PostgREST "column … does not exist" bzw.
 * "schema cache". Dann einmal ohne die neuen Spalten wiederholen, damit der
 * Katalog-Sync nicht an fehlenden Spalten scheitert.
 */
async function writeVariant(fn, payload) {
  const hasNewKeys = GEWICHT_GRUNDPREIS_KEYS.some((k) => k in payload);
  try {
    return await fn(payload);
  } catch (err) {
    if (!hasNewKeys || !/column|schema cache/i.test(err?.message || '')) throw err;
    const fallback = Object.fromEntries(
      Object.entries(payload).filter(([k]) => !GEWICHT_GRUNDPREIS_KEYS.includes(k)));
    return fn(fallback);
  }
}

/**
 * Varianten eines Produkts upserten — b2b_preis/bestand unberührt.
 *
 * @param skuOwner  Map sku(lowercase) → {productId, productName} über ALLE FC-Varianten.
 *                  Shopify lässt dieselbe SKU an mehreren Produkten zu, FieldConnect
 *                  nicht (uq_product_variants_sku). Gehört die SKU bereits einem
 *                  anderen Produkt, wird die Variante OHNE SKU gespiegelt (Titel,
 *                  Preis, Bild bleiben aktuell) und der Fall als Warnung gemeldet —
 *                  früher scheiterte dadurch der ganze Produkt-Sync.
 * @param warnings  Sammelliste für solche Fälle.
 */
async function upsertVariants(productId, shopifyProduct, fcVariants, skuOwner = new Map(), warnings = []) {
  const bySku = new Map(fcVariants.map((v) => [normSku(v.sku).toLowerCase(), v]));
  // Wiedererkennung primär über die Shopify-Varianten-ID: eine Variante ohne SKU
  // wurde früher bei jedem Lauf neu angelegt (doppelte Zeilen).
  const byShopifyId = new Map(
    fcVariants.filter((v) => v.shopify_variant_id).map((v) => [v.shopify_variant_id, v]));
  const seenIds = new Set();
  // Dieselbe SKU an zwei Varianten DESSELBEN Shopify-Produkts käme sonst zweimal an
  const inDiesemLauf = new Set();
  const nodes = shopifyProduct.variants?.nodes || [];

  for (let i = 0; i < nodes.length; i++) {
    const sv = nodes[i];
    const sku = normSku(sv.sku);
    const owner = sku ? skuOwner.get(sku.toLowerCase()) : null;
    const skuBlockiert = sku
      ? (!!owner && owner.productId !== productId) || inDiesemLauf.has(sku.toLowerCase())
      : false;
    if (skuBlockiert) {
      warnings.push(
        `${shopifyProduct.title}: SKU ${sku} gehört in FieldConnect bereits zu „${owner.productName}" `
        + '— in Shopify doppelt vergeben. Variante wurde ohne SKU übernommen.');
    }
    const options = Object.fromEntries((sv.selectedOptions || []).map((o) => [o.name, o.value]));
    const payload = {
      product_id: productId,
      sku: skuBlockiert ? null : (sku || null),
      title: sv.title === 'Default Title' ? 'Standard' : (sv.title || 'Standard'),
      options,
      online_preis: sv.price != null ? parseFloat(sv.price) : null,
      compare_at_preis: sv.compareAtPrice != null ? parseFloat(sv.compareAtPrice) : null,
      position: i + 1,
      aktiv: true,
      shopify_variant_id: sv.id,
      shopify_inventory_item_id: sv.inventoryItem?.id || null,
      updated_date: new Date().toISOString(),
    };
    // Gewicht/Grundpreis nur übernehmen, wenn Shopify echte Werte liefert —
    // lokale Angaben werden nie geleert. grundpreis_anzeigen ist in Shopify
    // nicht lesbar und bleibt lokal.
    const w = sv.inventoryItem?.measurement?.weight;
    const kg = weightToKg(w?.value, w?.unit);
    if (kg != null && kg > 0) {
      payload.gewicht_kg = kg;
      payload.gewicht_synced_at = new Date().toISOString();
    }
    const gp = fromShopifyUnitPrice(sv.unitPriceMeasurement);
    if (gp) Object.assign(payload, gp);

    const update = (id) => (p) => base44.entities.ProductVariant.update(id, p);
    const existing = byShopifyId.get(sv.id)
      || (!skuBlockiert && sku ? bySku.get(sku.toLowerCase()) : null);
    if (existing) {
      await writeVariant(update(existing.id), payload);
      seenIds.add(existing.id);
    } else {
      // Default-Variante ohne SKU adoptieren statt doppelt anlegen
      const orphan = fcVariants.find((v) => v.is_default && !normSku(v.sku) && !seenIds.has(v.id));
      if (orphan && nodes.length === 1) {
        await writeVariant(update(orphan.id), payload);
        seenIds.add(orphan.id);
      } else {
        const created = await writeVariant(
          (p) => base44.entities.ProductVariant.create(p),
          { ...payload, is_default: nodes.length === 1 });
        if (created?.id) seenIds.add(created.id);
      }
    }
    if (payload.sku) {
      const k = payload.sku.toLowerCase();
      inDiesemLauf.add(k);
      skuOwner.set(k, { productId, productName: shopifyProduct.title });
    }
  }
  // In Shopify entfernte Varianten deaktivieren (nie löschen)
  for (const v of fcVariants) {
    if (!seenIds.has(v.id) && v.shopify_variant_id) {
      await base44.entities.ProductVariant.update(v.id, { aktiv: false });
    }
  }
}

/**
 * SKU-Besitzkarte über ALLE Varianten: welche SKU gehört welchem Produkt?
 * Grundlage dafür, dass in Shopify doppelt vergebene SKUs den Sync nicht sprengen.
 */
async function buildSkuOwner() {
  const [variants, products] = await Promise.all([
    base44.entities.ProductVariant.list(),
    base44.entities.Product.list('name'),
  ]);
  const namen = new Map(products.map((p) => [p.id, p.name]));
  const owner = new Map();
  variants.forEach((v) => {
    const k = normSku(v.sku).toLowerCase();
    if (k && !owner.has(k)) owner.set(k, { productId: v.product_id, productName: namen.get(v.product_id) || '?' });
  });
  return owner;
}

/** Gematchte Produkte spiegeln (Inhalt runter — Operationsfelder unberührt) */
export async function applyMirror(matched, onProgress) {
  let done = 0;
  const errors = [];
  const warnings = [];
  const skuOwner = await buildSkuOwner();
  for (const m of matched) {
    try {
      const firstImage = await mirrorImages(m.fcProduct.id, m.shopify);
      await upsertVariants(m.fcProduct.id, m.shopify, m.fcVariants, skuOwner, warnings);
      const patch = {
        name: m.shopify.title || m.fcProduct.name,
        description_html: m.shopify.descriptionHtml || null,
        shopify_product_id: m.shopify.id,
        channel_shopify: true,
        content_synced_at: new Date().toISOString(),
      };
      if (firstImage) patch.image_url = firstImage;
      // Produkt-SKU heilen (leer oder als Konflikt markiert) → erste Varianten-SKU.
      // Nur, wenn diese SKU nicht schon einem anderen Produkt gehört: sonst trüge
      // das Produkt die Artikelnummer eines fremden Artikels.
      const firstSku = normSku(m.shopify.variants?.nodes?.[0]?.sku);
      const cur = normSku(m.fcProduct.sku);
      const besitzer = firstSku ? skuOwner.get(firstSku.toLowerCase()) : null;
      const fremd = !!besitzer && besitzer.productId !== m.fcProduct.id;
      if (firstSku && !fremd && (!cur || cur.endsWith('-KONFLIKT'))) patch.sku = firstSku;
      await base44.entities.Product.update(m.fcProduct.id, patch);
    } catch (err) {
      errors.push(`${m.shopify.title}: ${err.message}`);
    }
    done++;
    onProgress?.(done, matched.length);
  }
  return { done, errors, warnings };
}

/** Ausgewählte neue Shopify-Produkte in FC anlegen (nur nach Bestätigung) */
export async function applyImport(items, onProgress) {
  let done = 0;
  const errors = [];
  const warnings = [];
  const skuOwner = await buildSkuOwner();
  for (const item of items) {
    const sp = item.shopify;
    try {
      // Idempotenz: dieses Shopify-Produkt wurde schon einmal importiert?
      const already = await base44.entities.Product.filter({ shopify_product_id: sp.id });
      if (already && already.length > 0) {
        throw new Error(`bereits importiert (Produkt ${already[0].id}) — übersprungen`);
      }
      const skus = sp.variants.nodes.map((v) => normSku(v.sku)).filter(Boolean);
      // Sicherheitsnetz: existiert eine der SKUs bereits in FC → kein Import
      for (const sku of skus) {
        const hit = await base44.entities.ProductVariant.filter({ sku });
        if (hit && hit.length > 0) {
          throw new Error(`SKU ${sku} existiert bereits (Produkt ${hit[0].product_id}) — Import übersprungen`);
        }
      }
      const mainSku = skus[0] || null;
      const initialStock = sp.variants.nodes.reduce(
        (s, v) => s + (Number(v.inventoryQuantity) || 0), 0);
      const product = await base44.entities.Product.create({
        name: sp.title,
        sku: mainSku || null,
        description: '',
        description_html: sp.descriptionHtml || null,
        preis: null, // B2B-Preis bewusst leer — Entscheidung des Admins
        stock: initialStock,
        aktiv: true,
        is_active: true,
        shopify_product_id: sp.id,
        channel_shopify: true,
        content_synced_at: new Date().toISOString(),
      });
      try {
        const firstImage = await mirrorImages(product.id, sp);
        if (firstImage) await base44.entities.Product.update(product.id, { image_url: firstImage });
        await upsertVariants(product.id, sp, [], skuOwner, warnings);
      } catch (inner) {
        // Kompensation: gerade erst angelegtes Produkt ohne Varianten ist
        // unbrauchbar (kein Verlauf, keine Bewegungen) → wieder entfernen,
        // damit kein verwaister Datensatz zurückbleibt.
        await base44.entities.Product.delete(product.id).catch(() => {});
        throw inner;
      }
    } catch (err) {
      errors.push(`${sp.title}: ${err.message}`);
    }
    done++;
    onProgress?.(done, items.length);
  }
  return { done, errors, warnings };
}
